import { create } from 'zustand';
import apiClient from '../api/client';
import type { Conversation, ConversationMessage, ConversationQueryResult, ContextInfo } from '../types';

interface ConversationStore {
  // State
  conversations: Conversation[];
  currentConversation: Conversation | null;
  contextInfo: ContextInfo | null;
  isLoading: boolean;
  isQuerying: boolean;
  error: string | null;

  // Actions
  createConversation: (title?: string, documentIds?: string[]) => Promise<Conversation>;
  fetchConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  addMessage: (conversationId: string, content: string) => Promise<ConversationMessage>;
  queryInConversation: (
    conversationId: string,
    query: string,
    options?: {
      documentIds?: string[];
      topK?: number;
      useHybridSearch?: boolean;
      searchOptions?: {
        enableExpansion?: boolean;
        enableReRanking?: boolean;
        fusionMethod?: 'rrf' | 'weighted' | 'max';
        reRankAlgorithm?: 'mmr' | 'relevance' | 'metadata' | 'recency' | 'combined';
      };
    }
  ) => Promise<ConversationQueryResult>;
  updateTitle: (conversationId: string, title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  fetchContextInfo: (conversationId: string) => Promise<ContextInfo>;
  clearError: () => void;
  setCurrentConversation: (conversation: Conversation | null) => void;
}

export const useConversationStore = create<ConversationStore>((set, get) => ({
  // Initial state
  conversations: [],
  currentConversation: null,
  contextInfo: null,
  isLoading: false,
  isQuerying: false,
  error: null,

  // Create a new conversation
  createConversation: async (title?: string, documentIds?: string[]) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post('/api/conversations', {
        title: title || 'New Conversation',
        documentIds,
      });

      const conversation: Conversation = {
        ...response.data.conversation,
        createdAt: new Date(response.data.conversation.createdAt),
        updatedAt: new Date(response.data.conversation.updatedAt),
        messages: response.data.conversation.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      };

      set((state) => ({
        conversations: [conversation, ...state.conversations],
        currentConversation: conversation,
        isLoading: false,
      }));

      return conversation;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create conversation';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  // Fetch all conversations
  fetchConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/api/conversations');
      const conversations = response.data.conversations.map((conv: any) => ({
        ...conv,
        createdAt: new Date(conv.createdAt),
        updatedAt: new Date(conv.updatedAt),
        messages: conv.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      }));

      set({ conversations, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch conversations';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Load a specific conversation
  loadConversation: async (conversationId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/api/conversations/${conversationId}`);
      const conversation: Conversation = {
        ...response.data.conversation,
        createdAt: new Date(response.data.conversation.createdAt),
        updatedAt: new Date(response.data.conversation.updatedAt),
        messages: response.data.conversation.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      };

      set({ currentConversation: conversation, isLoading: false });

      // Also fetch context info
      await get().fetchContextInfo(conversationId);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to load conversation';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Add a message to conversation
  addMessage: async (conversationId: string, content: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.post(`/api/conversations/${conversationId}/messages`, {
        content,
      });

      const message: ConversationMessage = {
        ...response.data.message,
        timestamp: new Date(response.data.message.timestamp),
      };

      // Update current conversation
      set((state) => {
        if (state.currentConversation?.id === conversationId) {
          return {
            currentConversation: {
              ...state.currentConversation,
              messages: [...state.currentConversation.messages, message],
            },
            isLoading: false,
          };
        }
        return { isLoading: false };
      });

      return message;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to add message';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  // Query documents within conversation context
  queryInConversation: async (conversationId: string, query: string, options = {}) => {
    set({ isQuerying: true, error: null });
    try {
      const response = await apiClient.post(`/api/conversations/${conversationId}/query`, {
        content: query,
        ...options,
      });

      const result: ConversationQueryResult = {
        conversationId: response.data.conversationId,
        message: {
          ...response.data.message,
          timestamp: new Date(response.data.message.timestamp),
        },
        queryResults: response.data.queryResults,
        contextInfo: response.data.contextInfo,
      };

      // Update current conversation with new message
      set((state) => {
        if (state.currentConversation?.id === conversationId) {
          return {
            currentConversation: {
              ...state.currentConversation,
              messages: [...state.currentConversation.messages, result.message],
              totalTokens: result.contextInfo.totalTokens,
            },
            contextInfo: {
              totalMessages: result.contextInfo.totalMessages,
              totalTokens: result.contextInfo.totalTokens,
              contextWindowUsage: result.contextInfo.contextWindowUsage,
              remainingTokens: 8000 - result.contextInfo.totalTokens, // Estimate
              needsPruning: result.contextInfo.pruningOccurred || false,
            },
            isQuerying: false,
          };
        }
        return { isQuerying: false };
      });

      return result;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to query in conversation';
      set({ error: message, isQuerying: false });
      throw new Error(message);
    }
  },

  // Update conversation title
  updateTitle: async (conversationId: string, title: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.patch(`/api/conversations/${conversationId}`, { title });

      // Update conversation in state
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, title } : conv
        ),
        currentConversation:
          state.currentConversation?.id === conversationId
            ? { ...state.currentConversation, title }
            : state.currentConversation,
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update title';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Delete conversation
  deleteConversation: async (conversationId: string) => {
    set({ isLoading: true, error: null });
    try {
      await apiClient.delete(`/api/conversations/${conversationId}`);

      // Remove from state
      set((state) => ({
        conversations: state.conversations.filter((conv) => conv.id !== conversationId),
        currentConversation:
          state.currentConversation?.id === conversationId ? null : state.currentConversation,
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete conversation';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Fetch context window information
  fetchContextInfo: async (conversationId: string) => {
    try {
      const response = await apiClient.get(`/api/conversations/${conversationId}/context-info`);
      const contextInfo: ContextInfo = response.data.contextInfo;

      set({ contextInfo });
      return contextInfo;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to fetch context info';
      set({ error: message });
      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Set current conversation
  setCurrentConversation: (conversation: Conversation | null) => {
    set({ currentConversation: conversation });
  },
}));
